import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_META, tenantColor, TENANT_COLORS } from "@/lib/meta";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Building2, Users, Briefcase, LogIn, ChevronDown, Power, History, Palette, Pencil, Image as ImageIcon, Upload } from "lucide-react";

const logoUrl = (t) => (t.has_logo ? `${process.env.REACT_APP_BACKEND_URL}/api/tenants/${t.id}/logo?v=${t.logo_version || 0}` : null);

const emptyForm = { name: "", admin_name: "", admin_email: "", admin_password: "", color: null };

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function Tenants() {
  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [tenantUsers, setTenantUsers] = useState({});
  const [log, setLog] = useState([]);
  const [colorPickerId, setColorPickerId] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editName, setEditName] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);

  const load = useCallback(() => api.get("/tenants").then((r) => setTenants(r.data)), []);
  const loadLog = useCallback(() => api.get("/admin/impersonation-log").then((r) => setLog(r.data)).catch(() => {}), []);

  useEffect(() => {
    load();
    loadLog();
  }, [load, loadLog]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/tenants", form);
      toast.success("Tenant created");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (t) => {
    try {
      await api.put(`/tenants/${t.id}`, { active: !t.active });
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const setColor = async (t, color) => {
    try {
      await api.put(`/tenants/${t.id}`, { color: color || "" });
      setTenants((prev) => prev.map((x) => (x.id === t.id ? { ...x, color: color || null } : x)));
      setColorPickerId(null);
      toast.success(color ? "Tenant colour updated" : "Reset to auto colour");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const openEdit = (t) => {
    setEditTarget(t);
    setEditName(t.name);
  };

  const saveEdit = async () => {
    if (!editName.trim()) return;
    setEditSaving(true);
    try {
      await api.put(`/tenants/${editTarget.id}`, { name: editName.trim() });
      toast.success("Tenant updated");
      await load();
      setEditTarget((prev) => (prev ? { ...prev, name: editName.trim() } : prev));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setEditSaving(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file || !editTarget) return;
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/tenants/${editTarget.id}/logo`, fd);
      toast.success("Logo updated");
      await load();
      setEditTarget((prev) => (prev ? { ...prev, has_logo: true, logo_version: r.data.logo_version } : prev));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLogoBusy(false);
    }
  };

  const removeLogo = async () => {
    if (!editTarget) return;
    setLogoBusy(true);
    try {
      await api.delete(`/tenants/${editTarget.id}/logo`);
      toast.success("Logo removed");
      await load();
      setEditTarget((prev) => (prev ? { ...prev, has_logo: false } : prev));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLogoBusy(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget || confirmText.trim() !== deleteTarget.name) return;
    try {
      await api.delete(`/tenants/${deleteTarget.id}`);
      toast.success("Tenant deleted");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setDeleteTarget(null);
    }
  };

  const expand = async (t) => {
    if (expanded === t.id) {
      setExpanded(null);
      return;
    }
    setExpanded(t.id);
    if (!tenantUsers[t.id]) {
      const res = await api.get(`/tenants/${t.id}/users`);
      setTenantUsers((prev) => ({ ...prev, [t.id]: res.data }));
    }
  };

  const loginAs = async (u) => {
    try {
      await impersonate(u.id);
      toast.success(`Now viewing as ${u.name}`);
      navigate("/");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-5xl mx-auto">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-fuchsia-600">Platform</div>
          <h1 className="font-heading text-4xl font-black tracking-tighter text-brand-display mt-1">Tenants</h1>
          <p className="text-zinc-500 mt-1 text-sm">Create organisations and log in as any of their users.</p>
        </div>
        <button
          onClick={() => { setForm(emptyForm); setOpen(true); }}
          data-testid="new-tenant-button"
          className="bg-indigo-700 text-white rounded-full font-bold tracking-tight hover:bg-indigo-800 transition-colors px-5 py-2.5 flex items-center gap-2"
        >
          <Plus size={18} /> New Tenant
        </button>
      </div>

      <div className="space-y-3">
        {tenants.map((t) => (
          <div key={t.id} data-testid={`tenant-${t.id}`} className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
            <div className="flex items-center justify-between px-5 py-4">
              <button onClick={() => expand(t)} className="flex items-center gap-3 text-left" data-testid={`expand-tenant-${t.id}`}>
                <div className="h-10 w-10 text-white flex items-center justify-center rounded-md overflow-hidden" style={{ backgroundColor: t.has_logo ? "#fff" : (t.color || tenantColor(t.id)) }} data-testid={`tenant-color-${t.id}`}>
                  {t.has_logo ? <img src={logoUrl(t)} alt={t.name} className="h-full w-full object-contain p-0.5" /> : <Building2 size={18} />}
                </div>
                <div>
                  <div className="font-heading text-lg font-bold tracking-tight text-zinc-900 flex items-center gap-2">
                    {t.name}
                    {!t.active && <span className="text-[10px] font-bold uppercase bg-zinc-200 text-zinc-600 px-2 py-0.5">Inactive</span>}
                  </div>
                  <div className="text-xs text-zinc-500 flex items-center gap-3 mt-0.5">
                    <span className="flex items-center gap-1"><Users size={12} /> {t.user_count} users</span>
                    <span className="flex items-center gap-1"><Briefcase size={12} /> {t.job_count} jobs</span>
                  </div>
                </div>
              </button>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <button onClick={() => setColorPickerId(colorPickerId === t.id ? null : t.id)} data-testid={`tenant-color-btn-${t.id}`}
                    title="Set accent colour"
                    className="text-zinc-400 hover:text-zinc-900 transition-colors flex items-center gap-1">
                    <Palette size={16} />
                    <span className="w-3 h-3 rounded-full border border-zinc-300" style={{ backgroundColor: t.color || tenantColor(t.id) }} />
                  </button>
                  {colorPickerId === t.id && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setColorPickerId(null)} />
                      <div className="absolute right-0 mt-2 z-20 bg-white border border-zinc-200 rounded-lg shadow-xl p-3 w-52" data-testid={`tenant-color-panel-${t.id}`}>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-2">Accent colour</div>
                        <div className="grid grid-cols-6 gap-2">
                          {TENANT_COLORS.map((c) => (
                            <button key={c} onClick={() => setColor(t, c)} data-testid={`tenant-color-swatch-${t.id}-${c.replace('#','')}`}
                              className={`w-6 h-6 rounded-full transition-transform hover:scale-110 ${((t.color || "").toLowerCase() === c.toLowerCase()) ? "ring-2 ring-offset-2 ring-zinc-900" : ""}`}
                              style={{ backgroundColor: c }} title={c} />
                          ))}
                        </div>
                        <button onClick={() => setColor(t, null)} data-testid={`tenant-color-auto-${t.id}`}
                          className="mt-3 w-full text-xs font-semibold text-zinc-500 hover:text-zinc-900 flex items-center justify-center gap-1.5 border border-zinc-200 rounded py-1.5">
                          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: tenantColor(t.id) }} /> Auto {t.color ? "" : "(current)"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
                <button onClick={() => openEdit(t)} data-testid={`edit-tenant-${t.id}`}
                  title="Edit tenant"
                  className="text-zinc-400 hover:text-zinc-900 transition-colors">
                  <Pencil size={16} />
                </button>
                <button onClick={() => toggleActive(t)} data-testid={`toggle-tenant-${t.id}`}
                  className={`text-xs font-bold flex items-center gap-1 ${t.active ? "text-emerald-600" : "text-zinc-400"}`}>
                  <Power size={14} /> {t.active ? "Active" : "Off"}
                </button>
                <button onClick={() => { setDeleteTarget(t); setConfirmText(""); }} data-testid={`delete-tenant-${t.id}`}
                  className="text-zinc-400 hover:text-red-600 transition-colors">
                  <Trash2 size={16} />
                </button>
                <button onClick={() => expand(t)} className="text-zinc-400">
                  <ChevronDown size={18} className={`transition-transform ${expanded === t.id ? "rotate-180" : ""}`} />
                </button>
              </div>
            </div>

            {expanded === t.id && (
              <div className="border-t border-zinc-100 divide-y divide-zinc-50">
                {(tenantUsers[t.id] || []).map((u) => (
                  <div key={u.id} className="flex items-center justify-between px-5 py-2.5 hover:bg-zinc-50">
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-zinc-900 text-sm">{u.name}</span>
                      <span className="text-xs text-zinc-500 font-mono">{u.email}</span>
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${ROLE_META[u.role]?.cls || ""}`}>
                        {ROLE_META[u.role]?.label || u.role}
                      </span>
                    </div>
                    <button onClick={() => loginAs(u)} data-testid={`login-as-${u.id}`}
                      className="text-xs font-bold text-indigo-700 hover:text-indigo-900 flex items-center gap-1">
                      <LogIn size={14} /> Log in as
                    </button>
                  </div>
                ))}
                {(tenantUsers[t.id] || []).length === 0 && (
                  <div className="px-5 py-3 text-sm text-zinc-400">No users.</div>
                )}
              </div>
            )}
          </div>
        ))}
        {tenants.length === 0 && (
          <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] px-5 py-10 text-center text-zinc-400 text-sm">
            No tenants yet. Create your first organisation.
          </div>
        )}
      </div>

      {/* Impersonation history */}
      <div className="mt-10" data-testid="impersonation-log">
        <div className="flex items-center gap-2 mb-3">
          <History size={16} className="text-fuchsia-600" />
          <h2 className="font-heading text-lg font-bold tracking-tight text-zinc-900">Impersonation history</h2>
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] divide-y divide-zinc-50">
          {log.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-zinc-400">
              No one has been impersonated yet.
            </div>
          ) : (
            log.map((e) => (
              <div key={e.id} data-testid={`impersonation-log-${e.id}`} className="flex items-center justify-between px-5 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-9 w-9 bg-fuchsia-50 text-fuchsia-700 flex items-center justify-center rounded-md shrink-0">
                    <LogIn size={16} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm text-zinc-900">
                      <span className="font-bold">{e.super_admin_name || "Super admin"}</span>
                      <span className="text-zinc-500"> logged in as </span>
                      <span className="font-bold">{e.target_name}</span>
                    </div>
                    <div className="text-xs text-zinc-500 flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="font-mono">{e.target_email}</span>
                      {e.target_role && (
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border ${ROLE_META[e.target_role]?.cls || ""}`}>
                          {ROLE_META[e.target_role]?.label || e.target_role}
                        </span>
                      )}
                      {e.tenant_name && <span>· {e.tenant_name}</span>}
                    </div>
                  </div>
                </div>
                <span className="text-xs text-zinc-400 whitespace-nowrap ml-3">{timeAgo(e.at)}</span>
              </div>
            ))
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black tracking-tighter">New Tenant</DialogTitle>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">Organisation name</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="tenant-name-input"
                className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-indigo-700" />
            </div>
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">Accent colour <span className="text-zinc-400 font-normal">(optional — auto if left blank)</span></label>
              <div className="flex flex-wrap gap-2" data-testid="tenant-create-colors">
                {TENANT_COLORS.map((c) => (
                  <button type="button" key={c} onClick={() => setForm({ ...form, color: form.color === c ? null : c })}
                    data-testid={`tenant-create-color-${c.replace('#','')}`}
                    className={`w-7 h-7 rounded-full transition-transform hover:scale-110 ${form.color === c ? "ring-2 ring-offset-2 ring-zinc-900" : ""}`}
                    style={{ backgroundColor: c }} title={c} />
                ))}
              </div>
            </div>
            <div className="pt-2 border-t border-zinc-100">
              <div className="text-xs uppercase tracking-wider font-bold text-zinc-500 mb-2">Initial administrator</div>
              <div className="space-y-3">
                <input required placeholder="Admin name" value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
                  data-testid="tenant-admin-name-input"
                  className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-indigo-700" />
                <input required type="email" placeholder="Admin email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                  data-testid="tenant-admin-email-input"
                  className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-indigo-700" />
                <input required type="password" placeholder="Admin password (min 6)" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                  data-testid="tenant-admin-password-input"
                  className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-indigo-700" />
              </div>
            </div>
            <DialogFooter>
              <button type="submit" disabled={saving} data-testid="tenant-submit-button"
                className="bg-indigo-700 text-white rounded-full font-bold tracking-tight hover:bg-indigo-800 transition-colors px-6 py-2 disabled:opacity-60">
                {saving ? "Creating…" : "Create Tenant"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black tracking-tighter">Edit Tenant</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">Organisation name</label>
              <input value={editName} onChange={(e) => setEditName(e.target.value)}
                data-testid="edit-tenant-name-input"
                className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-indigo-700" />
              <button onClick={saveEdit} disabled={editSaving || !editName.trim()} data-testid="edit-tenant-save-button"
                className="mt-2 bg-indigo-700 text-white rounded-full font-bold text-sm tracking-tight hover:bg-indigo-800 transition-colors px-5 py-2 disabled:opacity-60">
                {editSaving ? "Saving…" : "Save name"}
              </button>
            </div>
            <div className="pt-4 border-t border-zinc-100">
              <div className="text-xs uppercase tracking-wider font-bold text-zinc-500 mb-2">Tenant logo</div>
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-md border border-zinc-200 bg-white flex items-center justify-center overflow-hidden shrink-0">
                  {editTarget?.has_logo
                    ? <img src={editTarget && logoUrl(editTarget)} alt="logo" className="h-full w-full object-contain p-1" data-testid="edit-tenant-logo-preview" />
                    : <ImageIcon size={22} className="text-zinc-300" />}
                </div>
                <div className="flex flex-col gap-2">
                  <label data-testid="edit-tenant-logo-upload"
                    className="inline-flex items-center gap-2 cursor-pointer bg-zinc-100 border border-zinc-300 rounded-full font-bold text-sm px-4 py-2 hover:bg-zinc-200">
                    <Upload size={15} /> {logoBusy ? "Uploading…" : "Upload logo"}
                    <input type="file" accept="image/*" className="hidden" disabled={logoBusy}
                      onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadLogo(f); }} />
                  </label>
                  {editTarget?.has_logo && (
                    <button onClick={removeLogo} disabled={logoBusy} data-testid="edit-tenant-logo-remove"
                      className="text-xs font-semibold text-red-600 hover:text-red-800 text-left">Remove logo</button>
                  )}
                  <span className="text-[11px] text-zinc-400">PNG, JPG, WEBP or SVG · up to 3MB</span>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the organisation and ALL its users, jobs, locations,
              statuses and activity. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-1">
            <label className="text-sm font-semibold text-zinc-700 block mb-1.5">
              Type <span className="font-mono font-bold text-red-600">{deleteTarget?.name}</span> to confirm
            </label>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              data-testid="delete-tenant-confirm-input"
              placeholder={deleteTarget?.name}
              autoFocus
              className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              disabled={confirmText.trim() !== deleteTarget?.name}
              data-testid="confirm-delete-tenant"
              className="bg-red-600 hover:bg-red-700 rounded-none disabled:opacity-40 disabled:pointer-events-none"
            >
              Delete everything
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
